import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  FileText, Code, List, Target, RefreshCw, TrendingUp, Users,
  Database, Globe, ChevronRight, Check, X, Settings, Save,
  Eye, Edit3, Copy, Trash2, Plus, Languages, Palette, Building2,
  AlertCircle, CheckCircle, Loader2, ChevronDown, ChevronUp,
  FileSignature, Mail, Phone, MapPin, Link as LinkIcon, GripVertical,
  Move, Type, AlignLeft
} from 'lucide-react';
import { toast } from '../../utils/toastUtils';
import { apiRequest } from '../../api/client';
import { useI18n } from '../../i18n';

// Icon mapping
const ICONS = {
  FileText, Code, List, Target, RefreshCw, TrendingUp, Users, Database, Globe
};

// Severity colors
const SEVERITY_COLORS = {
  Faible: 'bg-green-500/20 text-green-400 border-green-500/30',
  Moyen: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  Eleve: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  Critique: 'bg-red-500/20 text-red-400 border-red-500/30'
};

// Embeddable version for Account page
export function ReportTypesPanel({ embedded = false }) {
  const { t } = useI18n();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [reportTypes, setReportTypes] = useState([]);
  const [settings, setSettings] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [sections, setSections] = useState([]);
  const [language, setLanguage] = useState('fr');
  const [activeTab, setActiveTab] = useState('types'); // types, settings, branding
  const [error, setError] = useState(null);

  // Load report types and settings
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [typesData, settingsData] = await Promise.all([
        apiRequest('/api/v1/report-templates/types'),
        apiRequest('/api/v1/report-templates/settings')
      ]);
      setReportTypes(typesData);
      setSettings(settingsData);
    } catch (error) {
      console.error('Error loading report data:', error);
      setError(error.message || 'Erreur lors du chargement des données');
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const loadSections = async (reportType) => {
    try {
      const data = await apiRequest(`/api/v1/report-templates/types/${reportType}/sections?language=${language}`);
      setSections(data.sections || []);
    } catch (error) {
      console.error('Error loading sections:', error);
      toast.error('Erreur lors du chargement des sections');
    }
  };

  // Section update handler
  const handleUpdateSection = async (sectionKey, data) => {
    if (!selectedType) return;

    try {
      await apiRequest(`/api/v1/report-templates/types/${selectedType.id}/sections/${sectionKey}`, {
        method: 'PATCH',
        body: data
      });
      // Reload sections
      await loadSections(selectedType.id);
    } catch (error) {
      console.error('Error updating section:', error);
      if (error.message?.includes('company')) {
        toast.error('Vous devez appartenir à une entreprise pour personnaliser les sections');
      } else {
        throw error;
      }
    }
  };

  // Section delete handler
  const handleDeleteSection = async (sectionKey) => {
    if (!selectedType) return;
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette section ?')) return;

    try {
      await apiRequest(`/api/v1/report-templates/types/${selectedType.id}/sections/${sectionKey}`, {
        method: 'DELETE'
      });
      setSections(prev => prev.filter(s => s.section_key !== sectionKey));
      toast.success('Section supprimée');
    } catch (error) {
      console.error('Error deleting section:', error);
      if (error.message?.includes('company')) {
        toast.error('Vous devez appartenir à une entreprise pour supprimer des sections');
      } else {
        toast.error('Erreur lors de la suppression');
      }
    }
  };

  // Section reorder handlers
  const handleMoveSection = async (index, direction) => {
    if (!selectedType) return;
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= sections.length) return;

    const newSections = [...sections];
    [newSections[index], newSections[newIndex]] = [newSections[newIndex], newSections[index]];

    // Update order locally first for responsiveness
    setSections(newSections);

    try {
      // Save new order to backend
      const orderedKeys = newSections.map(s => s.section_key);
      await apiRequest(`/api/v1/report-templates/types/${selectedType.id}/sections/reorder`, {
        method: 'POST',
        body: { section_order: orderedKeys }
      });
    } catch (error) {
      console.error('Error reordering sections:', error);
      // Reload original order on error
      await loadSections(selectedType.id);
      toast.error('Erreur lors du réordonnancement');
    }
  };

  // Add new section handler
  const handleAddSection = async () => {
    if (!selectedType) return;

    const sectionKey = `custom_section_${Date.now()}`;
    const newSection = {
      section_key: sectionKey,
      title: 'Nouvelle section',
      description: '',
      is_required: false,
      is_enabled: true,
      content_template: ''
    };

    try {
      await apiRequest(`/api/v1/report-templates/types/${selectedType.id}/sections`, {
        method: 'POST',
        body: newSection
      });
      await loadSections(selectedType.id);
      toast.success('Section ajoutée');
    } catch (error) {
      console.error('Error adding section:', error);
      if (error.message?.includes('company')) {
        toast.error('Vous devez appartenir à une entreprise pour ajouter des sections');
      } else {
        toast.error('Erreur lors de l\'ajout');
      }
    }
  };

  const handleSelectType = async (type) => {
    setSelectedType(type);
    await loadSections(type.id);
  };

  const handleToggleReportType = async (typeId) => {
    if (!settings) return;

    const newActiveTypes = settings.active_report_types?.includes(typeId)
      ? settings.active_report_types.filter(t => t !== typeId)
      : [...(settings.active_report_types || []), typeId];

    try {
      const updated = await apiRequest('/api/v1/report-templates/settings', {
        method: 'PATCH',
        body: { active_report_types: newActiveTypes }
      });
      setSettings(updated);
      setReportTypes(prev => prev.map(t => ({
        ...t,
        is_active: newActiveTypes.includes(t.id)
      })));
      toast.success('Types de rapports mis à jour');
    } catch (error) {
      console.error('Error updating report types:', error);
      if (error.message?.includes('company')) {
        toast.error('Vous devez appartenir à une entreprise pour modifier les types de rapports');
      } else {
        toast.error('Erreur lors de la mise à jour');
      }
    }
  };

  const handleSaveSettings = async () => {
    if (!settings) return;

    try {
      setSaving(true);
      await apiRequest('/api/v1/report-templates/settings', {
        method: 'PATCH',
        body: settings
      });
      toast.success('Paramètres sauvegardés');
    } catch (error) {
      console.error('Error saving settings:', error);
      if (error.message?.includes('company')) {
        toast.error('Vous devez appartenir à une entreprise pour sauvegarder les paramètres');
      } else {
        toast.error('Erreur lors de la sauvegarde');
      }
    } finally {
      setSaving(false);
    }
  };

  const getIcon = (iconName) => {
    const Icon = ICONS[iconName] || FileText;
    return Icon;
  };

  if (loading) {
    return (
      <div className={`${embedded ? 'py-12' : 'min-h-screen bg-[#0a0a0a]'} flex items-center justify-center`}>
        <Loader2 className="w-8 h-8 text-brand-green animate-spin" />
        <span className="ml-3 text-gray-400">{t('common.loading')}</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`${embedded ? 'py-12' : 'min-h-screen bg-[#0a0a0a]'} flex flex-col items-center justify-center`}>
        <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
        <p className="text-gray-400 text-center">{t('errors.generic')}</p>
        <button
          onClick={loadData}
          className="mt-4 px-4 py-2 bg-brand-green text-black font-medium rounded-lg hover:bg-brand-green/90 transition-all"
        >
          {t('errors.retry')}
        </button>
      </div>
    );
  }

  const containerClass = embedded ? '' : 'min-h-screen bg-[#0a0a0a] p-6';

  return (
    <div className={containerClass}>
      {/* Header - only show if not embedded */}
      {!embedded && (
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-white mb-2">{t('reports.title')}</h1>
          <p className="text-gray-400">
            {t('reports.config.subtitle')}
          </p>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-gray-800 pb-4">
        <button
          onClick={() => setActiveTab('types')}
          className={`px-4 py-2 rounded-lg font-medium transition-all ${
            activeTab === 'types'
              ? 'bg-brand-green text-black'
              : 'text-gray-400 hover:text-white hover:bg-gray-800'
          }`}
        >
          <FileText className="w-4 h-4 inline mr-2" />
          {t('reports.reportType')}
        </button>
        <button
          onClick={() => setActiveTab('settings')}
          className={`px-4 py-2 rounded-lg font-medium transition-all ${
            activeTab === 'settings'
              ? 'bg-brand-green text-black'
              : 'text-gray-400 hover:text-white hover:bg-gray-800'
          }`}
        >
          <Settings className="w-4 h-4 inline mr-2" />
          {t('reports.settings')}
        </button>
        <button
          onClick={() => setActiveTab('branding')}
          className={`px-4 py-2 rounded-lg font-medium transition-all ${
            activeTab === 'branding'
              ? 'bg-brand-green text-black'
              : 'text-gray-400 hover:text-white hover:bg-gray-800'
          }`}
        >
          <Palette className="w-4 h-4 inline mr-2" />
          {t('reports.branding')}
        </button>
      </div>

      {/* Types Tab */}
      {activeTab === 'types' && (
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Report Types List */}
          <div className="lg:col-span-1 space-y-3">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white">{t('reports.reportType')}</h2>
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                className="bg-[#111] border border-gray-700 rounded-lg px-3 py-1.5 text-sm text-white"
              >
                <option value="fr">Français</option>
                <option value="en">English</option>
              </select>
            </div>

            {reportTypes.length === 0 ? (
              <div className="bg-[#111] rounded-xl border border-gray-800 border-dashed p-6 text-center">
                <FileText className="w-8 h-8 text-gray-500 mx-auto mb-3" />
                <p className="text-gray-400 text-sm">{t('reports.config.noReportTypes')}</p>
              </div>
            ) : (
              reportTypes.map((type) => {
                const Icon = getIcon(type.icon);
                const isSelected = selectedType?.id === type.id;

                return (
                  <div
                    key={type.id}
                    onClick={() => handleSelectType(type)}
                    className={`p-4 rounded-xl border cursor-pointer transition-all ${
                      isSelected
                        ? 'bg-brand-green/10 border-brand-green/50'
                        : 'bg-[#111] border-gray-800 hover:border-gray-700'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          type.is_active ? 'bg-brand-green/20' : 'bg-gray-700/50'
                        }`}>
                          <Icon className={`w-5 h-5 ${type.is_active ? 'text-brand-green' : 'text-gray-500'}`} />
                        </div>
                        <div>
                          <h3 className="font-medium text-white">
                            {type.name?.[language] || type.name?.fr || type.id}
                          </h3>
                          <p className="text-xs text-gray-400 mt-0.5 truncate">
                            {type.target_audience?.[language] || type.target_audience?.fr || ''}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleReportType(type.id);
                        }}
                        className={`w-6 h-6 rounded-full flex items-center justify-center transition-all ${
                          type.is_active
                            ? 'bg-brand-green text-black'
                            : 'bg-gray-700 text-gray-400'
                        }`}
                      >
                        {type.is_active ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-2 line-clamp-2">
                      {type.description?.[language] || type.description?.fr || ''}
                    </p>
                  </div>
                );
              })
            )}
          </div>

          {/* Section Details */}
          <div className="lg:col-span-2">
            {selectedType ? (
              <div className="bg-[#111] rounded-xl border border-gray-800 p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-xl font-semibold text-white">
                      {selectedType.name?.[language] || selectedType.name?.fr || selectedType.id}
                    </h2>
                    <p className="text-gray-400 text-sm mt-1 line-clamp-3">
                      {selectedType.description?.[language] || selectedType.description?.fr || ''}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button className="p-2 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white transition-all">
                      <Eye className="w-4 h-4" />
                    </button>
                    <button className="p-2 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white transition-all">
                      <Edit3 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wider">
                    {t('reports.config.sectionsTitle')} ({sections.length})
                  </h3>
                  <button
                    onClick={handleAddSection}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-brand-green/20 text-brand-green rounded-lg hover:bg-brand-green/30 transition-all"
                  >
                    <Plus className="w-4 h-4" />
                    {t('reports.config.addSection')}
                  </button>
                </div>

                <div className="space-y-3">
                  {sections.map((section, index) => (
                    <SectionCard
                      key={section.section_key}
                      section={section}
                      index={index}
                      language={language}
                      onUpdate={handleUpdateSection}
                      onDelete={handleDeleteSection}
                      onMoveUp={(idx) => handleMoveSection(idx, 'up')}
                      onMoveDown={(idx) => handleMoveSection(idx, 'down')}
                      isFirst={index === 0}
                      isLast={index === sections.length - 1}
                    />
                  ))}
                </div>
              </div>
            ) : (
              <div className="bg-[#111] rounded-xl border border-gray-800 border-dashed p-12 text-center flex flex-col items-center justify-center min-h-[300px]">
                <div className="w-16 h-16 rounded-full bg-gray-800/50 flex items-center justify-center mb-4">
                  <FileText className="w-8 h-8 text-gray-500" />
                </div>
                <p className="text-gray-300 font-medium text-lg mb-2">
                  {t('reports.config.noTypeSelected')}
                </p>
                <p className="text-gray-400 text-base max-w-xs">
                  {t('reports.config.noTypeSelectedHint')}
                </p>
                <div className="flex items-center gap-2 mt-4 text-gray-500">
                  <ChevronRight className="w-4 h-4 rotate-180" />
                  <span className="text-sm">{t('reports.reportType')}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && settings && (
        <div className="max-w-3xl">
          <div className="bg-[#111] rounded-xl border border-gray-800 p-6 space-y-6">
            <h2 className="text-lg font-semibold text-white mb-4">{t('reports.config.settingsTitle')}</h2>

            {/* Language Settings */}
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                <Languages className="w-4 h-4 inline mr-2" />
                {t('reports.config.defaultLanguage')}
              </label>
              <select
                value={settings.default_language}
                onChange={(e) => setSettings({...settings, default_language: e.target.value})}
                className="w-full bg-[#0a0a0a] border border-gray-700 rounded-lg px-4 py-2.5 text-white"
              >
                <option value="fr">Français</option>
                <option value="en">English</option>
                <option value="es">Español</option>
                <option value="de">Deutsch</option>
              </select>
            </div>

            {/* Confidentiality */}
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                {t('reports.config.confidentialityLevel')}
              </label>
              <select
                value={settings.confidentiality_level || 'Confidentiel'}
                onChange={(e) => setSettings({...settings, confidentiality_level: e.target.value})}
                className="w-full bg-[#0a0a0a] border border-gray-700 rounded-lg px-4 py-2.5 text-white"
              >
                <option value="Public">Public</option>
                <option value="Interne">Interne</option>
                <option value="Confidentiel">Confidentiel</option>
                <option value="Secret">Secret</option>
                <option value="Très Secret">Très Secret</option>
              </select>
            </div>

            {/* Include Options */}
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-400 mb-2">
                {t('reports.config.includeOptions')}
              </label>

              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.include_logo}
                  onChange={(e) => setSettings({...settings, include_logo: e.target.checked})}
                  className="w-5 h-5 rounded border-gray-600 bg-[#0a0a0a] text-brand-green focus:ring-brand-green flex-shrink-0 mt-0.5"
                />
                <span className="text-white">{t('reports.config.includeLogo')}</span>
              </label>

              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.include_table_of_contents}
                  onChange={(e) => setSettings({...settings, include_table_of_contents: e.target.checked})}
                  className="w-5 h-5 rounded border-gray-600 bg-[#0a0a0a] text-brand-green focus:ring-brand-green flex-shrink-0 mt-0.5"
                />
                <span className="text-white">{t('reports.config.includeTableOfContents')}</span>
              </label>

              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.include_appendices}
                  onChange={(e) => setSettings({...settings, include_appendices: e.target.checked})}
                  className="w-5 h-5 rounded border-gray-600 bg-[#0a0a0a] text-brand-green focus:ring-brand-green flex-shrink-0 mt-0.5"
                />
                <span className="text-white">{t('reports.config.includeAppendices')}</span>
              </label>

              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.include_methodology}
                  onChange={(e) => setSettings({...settings, include_methodology: e.target.checked})}
                  className="w-5 h-5 rounded border-gray-600 bg-[#0a0a0a] text-brand-green focus:ring-brand-green flex-shrink-0 mt-0.5"
                />
                <span className="text-white">{t('reports.config.includeMethodology')}</span>
              </label>
            </div>

            {/* Footer Text */}
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                {t('reports.config.footerTextLabel')}
              </label>
              <textarea
                value={settings.footer_text || ''}
                onChange={(e) => setSettings({...settings, footer_text: e.target.value})}
                placeholder={t('reports.placeholders.footerText')}
                rows={2}
                className="w-full bg-[#0a0a0a] border border-gray-700 rounded-lg px-4 py-2.5 text-white placeholder-gray-400 resize-none"
              />
            </div>

            <button
              onClick={handleSaveSettings}
              disabled={saving}
              className="flex items-center gap-2 px-6 py-2.5 bg-brand-green hover:bg-brand-green/90 text-black font-medium rounded-lg transition-all disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {t('common.save')}
            </button>
          </div>
        </div>
      )}

      {/* Branding Tab */}
      {activeTab === 'branding' && settings && (
        <div className="max-w-3xl">
          <div className="bg-[#111] rounded-xl border border-gray-800 p-6 space-y-6">
            <h2 className="text-lg font-semibold text-white mb-4">{t('reports.config.brandingTitle')}</h2>

            {/* Logo URL */}
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                {t('reports.config.logoUrlLabel')}
              </label>
              <input
                type="url"
                value={settings.logo_url || ''}
                onChange={(e) => setSettings({...settings, logo_url: e.target.value})}
                placeholder={t('reports.placeholders.logoUrl')}
                className="w-full bg-[#0a0a0a] border border-gray-700 rounded-lg px-4 py-2.5 text-white placeholder-gray-400"
              />
            </div>

            {/* Colors */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  {t('reports.config.primaryColor')}
                </label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={settings.primary_color || '#00FF88'}
                    onChange={(e) => setSettings({...settings, primary_color: e.target.value})}
                    className="w-12 h-10 rounded border border-gray-700 bg-transparent cursor-pointer"
                  />
                  <input
                    type="text"
                    value={settings.primary_color || '#00FF88'}
                    onChange={(e) => setSettings({...settings, primary_color: e.target.value})}
                    className="flex-1 bg-[#0a0a0a] border border-gray-700 rounded-lg px-4 py-2 text-white"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  {t('reports.config.secondaryColor')}
                </label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={settings.secondary_color || '#1a1a2e'}
                    onChange={(e) => setSettings({...settings, secondary_color: e.target.value})}
                    className="w-12 h-10 rounded border border-gray-700 bg-transparent cursor-pointer"
                  />
                  <input
                    type="text"
                    value={settings.secondary_color || '#1a1a2e'}
                    onChange={(e) => setSettings({...settings, secondary_color: e.target.value})}
                    className="flex-1 bg-[#0a0a0a] border border-gray-700 rounded-lg px-4 py-2 text-white"
                  />
                </div>
              </div>
            </div>

            {/* Company Info */}
            <div className="border-t border-gray-800 pt-6">
              <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-4">
                <Building2 className="w-4 h-4 inline mr-2" />
                {t('reports.config.companyInfo')}
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">{t('reports.config.companyName')}</label>
                  <input
                    type="text"
                    value={settings.company_name_display || ''}
                    onChange={(e) => setSettings({...settings, company_name_display: e.target.value})}
                    className="w-full bg-[#0a0a0a] border border-gray-700 rounded-lg px-4 py-2.5 text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-1">
                    <MapPin className="w-3 h-3 inline mr-1" />
                    {t('reports.config.address')}
                  </label>
                  <textarea
                    value={settings.company_address || ''}
                    onChange={(e) => setSettings({...settings, company_address: e.target.value})}
                    rows={2}
                    className="w-full bg-[#0a0a0a] border border-gray-700 rounded-lg px-4 py-2.5 text-white resize-none"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">
                      <Mail className="w-3 h-3 inline mr-1" />
                      {t('reports.config.contactEmail')}
                    </label>
                    <input
                      type="email"
                      placeholder={t('reports.placeholders.contactEmail')}
                      value={settings.company_contact_email || ''}
                      onChange={(e) => setSettings({...settings, company_contact_email: e.target.value})}
                      className="w-full bg-[#0a0a0a] border border-gray-700 rounded-lg px-4 py-2.5 text-white placeholder-gray-400"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">
                      <Phone className="w-3 h-3 inline mr-1" />
                      {t('reports.config.phone')}
                    </label>
                    <input
                      type="tel"
                      value={settings.company_contact_phone || ''}
                      onChange={(e) => setSettings({...settings, company_contact_phone: e.target.value})}
                      className="w-full bg-[#0a0a0a] border border-gray-700 rounded-lg px-4 py-2.5 text-white"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-1">
                    <LinkIcon className="w-3 h-3 inline mr-1" />
                    {t('reports.config.website')}
                  </label>
                  <input
                    type="url"
                    value={settings.company_website || ''}
                    onChange={(e) => setSettings({...settings, company_website: e.target.value})}
                    className="w-full bg-[#0a0a0a] border border-gray-700 rounded-lg px-4 py-2.5 text-white"
                  />
                </div>
              </div>
            </div>

            <button
              onClick={handleSaveSettings}
              disabled={saving}
              className="flex items-center gap-2 px-6 py-2.5 bg-brand-green hover:bg-brand-green/90 text-black font-medium rounded-lg transition-all disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {t('common.save')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Default export for standalone page - just wraps the panel
export default function ReportTypes() {
  return <ReportTypesPanel embedded={false} />;
}

// Section Card Component with Edit capabilities
function SectionCard({ section, index, language, onUpdate, onDelete, onMoveUp, onMoveDown, isFirst, isLast }) {
  const { t } = useI18n();
  const [expanded, setExpanded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    title: section.title || section.title_translations?.[language] || section.title_translations?.fr || '',
    description: section.description || '',
    is_required: section.is_required || false,
    is_enabled: section.is_enabled !== false,
    content_template: section.content_template || ''
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onUpdate(section.section_key, editData);
      setIsEditing(false);
      toast.success('Section mise à jour');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditData({
      title: section.title || section.title_translations?.[language] || section.title_translations?.fr || '',
      description: section.description || '',
      is_required: section.is_required || false,
      is_enabled: section.is_enabled !== false,
      content_template: section.content_template || ''
    });
    setIsEditing(false);
  };

  return (
    <div className={`bg-[#0a0a0a] rounded-lg border overflow-hidden transition-all ${
      section.is_enabled !== false ? 'border-gray-800' : 'border-gray-800/50 opacity-60'
    }`}>
      <div className="flex items-center justify-between p-4">
        <div className="flex items-center gap-3 flex-1">
          {/* Drag handle */}
          <div className="flex flex-col gap-0.5">
            <button
              onClick={() => onMoveUp && onMoveUp(index)}
              disabled={isFirst}
              className={`p-1 rounded transition-all ${
                isFirst ? 'text-gray-700 cursor-not-allowed' : 'text-gray-500 hover:text-white hover:bg-gray-800'
              }`}
              title="Monter"
            >
              <ChevronUp className="w-3 h-3" />
            </button>
            <button
              onClick={() => onMoveDown && onMoveDown(index)}
              disabled={isLast}
              className={`p-1 rounded transition-all ${
                isLast ? 'text-gray-700 cursor-not-allowed' : 'text-gray-500 hover:text-white hover:bg-gray-800'
              }`}
              title="Descendre"
            >
              <ChevronDown className="w-3 h-3" />
            </button>
          </div>

          {/* Index badge */}
          <span className={`w-6 h-6 rounded-full text-xs flex items-center justify-center font-medium ${
            section.is_enabled !== false ? 'bg-brand-green/20 text-brand-green' : 'bg-gray-700/50 text-gray-500'
          }`}>
            {index + 1}
          </span>

          {/* Content */}
          <div className="flex-1 min-w-0">
            {isEditing ? (
              <div className="space-y-3">
                <input
                  type="text"
                  value={editData.title}
                  onChange={(e) => setEditData({...editData, title: e.target.value})}
                  placeholder={t('reports.placeholders.sectionTitle')}
                  className="w-full bg-[#111] border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:border-brand-green focus:ring-1 focus:ring-brand-green"
                />
                <textarea
                  value={editData.description}
                  onChange={(e) => setEditData({...editData, description: e.target.value})}
                  placeholder={t('reports.placeholders.sectionDescription')}
                  rows={2}
                  className="w-full bg-[#111] border border-gray-700 rounded-lg px-3 py-2 text-white text-sm resize-none focus:border-brand-green focus:ring-1 focus:ring-brand-green"
                />
                <div className="flex flex-wrap items-center gap-4">
                  <label className="flex items-center gap-2.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editData.is_required}
                      onChange={(e) => setEditData({...editData, is_required: e.target.checked})}
                      className="w-4 h-4 rounded border-gray-600 bg-[#0a0a0a] text-brand-green focus:ring-brand-green flex-shrink-0"
                    />
                    <span className="text-sm text-gray-400 whitespace-nowrap">{t('reports.config.sectionRequired')}</span>
                  </label>
                  <label className="flex items-center gap-2.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editData.is_enabled}
                      onChange={(e) => setEditData({...editData, is_enabled: e.target.checked})}
                      className="w-4 h-4 rounded border-gray-600 bg-[#0a0a0a] text-brand-green focus:ring-brand-green flex-shrink-0"
                    />
                    <span className="text-sm text-gray-400 whitespace-nowrap">{t('reports.config.sectionActive')}</span>
                  </label>
                </div>
              </div>
            ) : (
              <div onClick={() => setExpanded(!expanded)} className="cursor-pointer">
                <h4 className="font-medium text-white truncate">
                  {section.title || section.title_translations?.[language] || section.title_translations?.fr || section.section_key}
                </h4>
                {section.description && (
                  <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">
                    {section.description}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 ml-3 flex-shrink-0">
          {section.is_required && !isEditing && (
            <span className="px-2 py-0.5 text-xs bg-red-500/20 text-red-400 rounded whitespace-nowrap">
              {t('reports.config.requiredBadge')}
            </span>
          )}

          {isEditing ? (
            <>
              <button
                onClick={handleCancel}
                className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-gray-800 transition-all"
                title={t('common.cancel')}
              >
                <X className="w-4 h-4" />
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="p-2 rounded-lg bg-brand-green/20 text-brand-green hover:bg-brand-green/30 transition-all disabled:opacity-50"
                title={t('common.save')}
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => setIsEditing(true)}
                className="p-2 rounded-lg text-gray-400 hover:text-brand-green hover:bg-gray-800 transition-all"
                title={t('common.edit')}
              >
                <Edit3 className="w-4 h-4" />
              </button>
              <button
                onClick={() => onDelete && onDelete(section.section_key)}
                className="p-2 rounded-lg text-gray-400 hover:text-red-400 hover:bg-gray-800 transition-all"
                title={t('common.delete')}
              >
                <Trash2 className="w-4 h-4" />
              </button>
              <button
                onClick={() => setExpanded(!expanded)}
                className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-gray-800 transition-all"
              >
                {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Expanded content */}
      {expanded && !isEditing && (
        <div className="px-4 pb-4 border-t border-gray-800 pt-3 space-y-4">
          {/* Content template editor */}
          <div>
            <label className="flex items-center gap-2 text-xs text-gray-400 mb-2">
              <AlignLeft className="w-3 h-3" />
              {t('reports.config.contentTemplateLabel')}
            </label>
            <ContentTemplateEditor
              value={section.content_template || ''}
              onSave={async (value) => {
                await onUpdate(section.section_key, { ...editData, content_template: value });
              }}
            />
          </div>

          {section.content_schema && (
            <div>
              <p className="text-xs text-gray-400 mb-2">Schéma de données :</p>
              <pre className="text-xs text-gray-500 bg-[#111] p-3 rounded-lg overflow-x-auto max-h-40">
                {JSON.stringify(section.content_schema, null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Content Template Editor Component
function ContentTemplateEditor({ value, onSave }) {
  const { t } = useI18n();
  const [content, setContent] = useState(value);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(content);
      setIsEditing(false);
      toast.success('Modèle sauvegardé');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  if (!isEditing) {
    return (
      <div
        onClick={() => setIsEditing(true)}
        className="bg-[#111] border border-gray-700 rounded-lg p-3 cursor-pointer hover:border-gray-600 transition-all min-h-[80px] group"
      >
        {content ? (
          <pre className="text-sm text-gray-300 whitespace-pre-wrap font-mono">{content}</pre>
        ) : (
          <p className="text-sm text-gray-600 italic">{t('reports.config.clickToAddTemplate')}</p>
        )}
        <div className="hidden group-hover:flex items-center justify-center absolute inset-0 bg-black/40 rounded-lg">
          <Edit3 className="w-5 h-5 text-white" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <textarea
        value={content}
        onChange={(e) => setContent(e.target.value)}
        placeholder={t('reports.placeholders.contentTemplate')}
        rows={8}
        className="w-full bg-[#0a0a0a] border border-gray-700 rounded-lg px-3 py-2 text-sm text-white font-mono resize-y focus:border-brand-green focus:ring-1 focus:ring-brand-green"
        autoFocus
      />
      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-500">
          {t('reports.config.markdownHint')}
        </p>
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              setContent(value);
              setIsEditing(false);
            }}
            className="px-4 py-2 text-gray-400 hover:text-white rounded-lg hover:bg-gray-800 transition-all font-medium"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-brand-green text-black font-medium rounded-lg hover:bg-brand-green/90 transition-all disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {t('common.save')}
          </button>
        </div>
      </div>
    </div>
  );
}
